/**
 * Notification Context
 * 
 * Provides notification state management and methods for the notification system.
 * Manages unread count, notification history, and preferences.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import type { UserNotification, NotificationPreferences } from "@nordex/core";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { getStoredToken } from "@/backend/auth";

import { API_BASE_URL } from "@/backend/config";

interface NotificationContextValue {
  // Notification history
  notifications: UserNotification[];
  isLoadingNotifications: boolean;
  unreadCount: number;
  
  // Sheet state
  isSheetOpen: boolean;
  openSheet: () => void;
  closeSheet: () => void;
  
  // Notification actions
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refetchNotifications: () => void;
  
  // Preferences
  preferences: NotificationPreferences | null;
  isLoadingPreferences: boolean;
  updatePreferences: (prefs: Partial<NotificationPreferences>) => Promise<void>;
  
  // Preferences modal state
  isPreferencesModalOpen: boolean;
  openPreferencesModal: () => void;
  closePreferencesModal: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const { userId, isAuthenticated } = useUnifiedAuth();
  const queryClient = useQueryClient();
  
  // Sheet state
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  
  // Preferences modal state
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  
  // Fetch notifications
  const {
    data: notifications = [],
    isLoading: isLoadingNotifications,
    refetch: refetchNotifications,
  } = useQuery({
    queryKey: ["user-notifications", userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const token = getStoredToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/notifications?limit=100`, {
        headers,
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch notifications");
      }
      
      return response.json() as Promise<UserNotification[]>;
    },
    enabled: isAuthenticated && !!userId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
  
  // Calculate unread count
  const unreadCount = notifications.filter((n) => !n.read).length;
  
  // Fetch preferences
  const {
    data: preferences = null,
    isLoading: isLoadingPreferences,
  } = useQuery({
    queryKey: ["notification-preferences", userId],
    queryFn: async () => {
      if (!userId) return null;
      
      const token = getStoredToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/notification-preferences`, {
        headers,
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch notification preferences");
      }
      
      return response.json() as Promise<NotificationPreferences>;
    },
    enabled: isAuthenticated && !!userId,
  });
  
  // Mark notification as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = getStoredToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/notifications/${id}/read`, {
        method: "POST",
        headers,
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to mark notification as read");
      }
      
      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch notifications
      queryClient.invalidateQueries({ queryKey: ["user-notifications", userId] });
    },
  });
  
  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const token = getStoredToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/notifications/read-all`, {
        method: "POST",
        headers,
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to mark all notifications as read");
      }
      
      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch notifications
      queryClient.invalidateQueries({ queryKey: ["user-notifications", userId] });
    },
  });
  
  // Update preferences mutation
  const updatePreferencesMutation = useMutation({
    mutationFn: async (prefs: Partial<NotificationPreferences>) => {
      const token = getStoredToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/notification-preferences`, {
        method: "PUT",
        headers,
        credentials: "include",
        body: JSON.stringify(prefs),
      });
      
      if (!response.ok) {
        throw new Error("Failed to update notification preferences");
      }
      
      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch preferences
      queryClient.invalidateQueries({ queryKey: ["notification-preferences", userId] });
    },
  });
  
  // Sheet controls
  const openSheet = useCallback(() => setIsSheetOpen(true), []);
  const closeSheet = useCallback(() => setIsSheetOpen(false), []);
  
  // Preferences modal controls
  const openPreferencesModal = useCallback(() => setIsPreferencesModalOpen(true), []);
  const closePreferencesModal = useCallback(() => setIsPreferencesModalOpen(false), []);
  
  // Mark as read handler
  const markAsRead = useCallback(
    async (id: string) => {
      await markAsReadMutation.mutateAsync(id);
    },
    [markAsReadMutation]
  );
  
  // Mark all as read handler
  const markAllAsRead = useCallback(async () => {
    await markAllAsReadMutation.mutateAsync();
  }, [markAllAsReadMutation]);
  
  // Update preferences handler
  const updatePreferences = useCallback(
    async (prefs: Partial<NotificationPreferences>) => {
      await updatePreferencesMutation.mutateAsync(prefs);
    },
    [updatePreferencesMutation]
  );
  
  const value: NotificationContextValue = {
    notifications,
    isLoadingNotifications,
    unreadCount,
    isSheetOpen,
    openSheet,
    closeSheet,
    markAsRead,
    markAllAsRead,
    refetchNotifications,
    preferences,
    isLoadingPreferences,
    updatePreferences,
    isPreferencesModalOpen,
    openPreferencesModal,
    closePreferencesModal,
  };
  
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
};

